import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import ClientPipeline from './ClientPipeline';
import ClientMetrics from './ClientMetrics';
import ClientRoles from './ClientRoles';
import ClientNPS from './ClientNPS';
import { fetchData } from '../../utils/api';

const BRAND_BLUE = '#4841F0';

const ClientDashboard = () => {
  const { clientName, clientEmail, clientLogout, isClientAuthenticated, isLoading: isAuthLoading } = useClientAuth();
  const navigate = useNavigate();

  const [candidatesData, setCandidatesData] = useState([]);
  const [npsData, setNpsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  // Fetch and filter data for this client
  useEffect(() => {
    const loadClientData = async () => {
      setIsLoading(true);
      try {
        const [candidates, nps] = await Promise.all([
          fetchData('candidates').catch(() => []),
          fetchData('nps').catch(() => [])
        ]);

        // Filter to only this client's data
        const clientCandidates = candidates.filter(
          c => c.client && c.client.toLowerCase() === clientName.toLowerCase()
        );
        const clientNps = nps.filter(
          n => n.client && n.client.toLowerCase() === clientName.toLowerCase()
        );

        setCandidatesData(clientCandidates);
        setNpsData(clientNps);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading client data:', err);
        setError('Unable to load your recruitment data. Please try refreshing the page.');
        setIsLoading(false);
      }
    };

    if (clientName) {
      loadClientData();
    }
  }, [clientName]);

  const handleLogout = () => {
    navigate('/client-logout', { replace: true });
  };

  // Wait for session restore before deciding auth state
  if (isAuthLoading) return null;
  if (!isClientAuthenticated) return <Navigate to="/login" replace />;

  // Compute KPIs
  const hiredCount = candidatesData.filter(c => c.stage === 'Hired').length;
  const totalCandidates = candidatesData.length;
  const hiredWithTime = candidatesData.filter(c => c.stage === 'Hired' && c.timeToHire && c.timeToHire > 0);
  const avgTimeToHire = hiredWithTime.length > 0
    ? Math.round(hiredWithTime.reduce((sum, c) => sum + (parseInt(c.timeToHire) || 0), 0) / hiredWithTime.length)
    : 0;

  // Get unique roles/departments
  const activeRoles = [...new Set(candidatesData.map(c => c.department || c.role).filter(Boolean))];

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-light)' }}>
        <header style={{ background: BRAND_BLUE }} className="shadow-md">
          <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
            <span style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 800,
              fontSize: '1.75rem',
              color: '#ffffff',
              letterSpacing: '-0.02em',
            }}>Satellite</span>
          </div>
        </header>
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 72px)' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: BRAND_BLUE }}></div>
            <p className="text-gray-600 font-medium">Loading your recruitment data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-light)' }}>
      {/* Header */}
      <header style={{ background: BRAND_BLUE }} className="shadow-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center space-x-3">
              <span style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 800,
                fontSize: '1.75rem',
                color: '#ffffff',
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}>Satellite</span>
              <span style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.8rem',
                fontWeight: 500,
                borderLeft: '1px solid rgba(255,255,255,0.3)',
                paddingLeft: '12px',
                marginLeft: '4px',
              }}>Client Portal</span>
            </div>

            {/* Navigation */}
            <nav className="flex items-center">
              <div className="flex rounded-lg p-1 space-x-1" style={{ background: 'rgba(255,255,255,0.15)' }}>
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'pipeline', label: 'Pipeline' },
                  { key: 'roles', label: 'Roles' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setActiveSection(key)}
                    className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200"
                    style={{
                      background: activeSection === key ? '#ffffff' : 'transparent',
                      color: activeSection === key ? BRAND_BLUE : 'rgba(255,255,255,0.85)',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* User info + logout */}
              <div className="ml-3 sm:ml-4 flex items-center space-x-2">
                <div className="hidden sm:block text-right">
                  <p className="text-xs font-medium" style={{ color: '#ffffff' }}>{clientName}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{clientEmail}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg transition-all duration-200"
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                  title="Sign out"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Welcome Banner */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Welcome, {clientName}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Your live recruitment overview &mdash; updated in real time
              </p>
            </div>
            {error && (
              <div className="mt-3 sm:mt-0 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-xs text-amber-700">{error}</p>
              </div>
            )}
          </div>
        </div>

        {activeSection === 'overview' && (
          <div className="fade-in">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <KPICard
                label="Total Candidates"
                value={totalCandidates}
                badge="Candidates"
                badgeClass="bg-blue-100 text-blue-800"
                accentColor="#4841F0"
                gradient="linear-gradient(135deg, #4841F0 0%, #3730c8 100%)"
                icon="users"
              />
              <KPICard
                label="Hired"
                value={hiredCount}
                badge="Placed"
                badgeClass="bg-green-100 text-green-800"
                accentColor="#10b981"
                gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                icon="check"
              />
              <KPICard
                label="Avg. Time to Hire"
                value={avgTimeToHire > 0 ? `${avgTimeToHire}` : '—'}
                badge="Days"
                badgeClass="bg-green-100 text-green-800"
                accentColor="#10b981"
                gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                icon="clock"
              />
              <KPICard
                label="Active Roles"
                value={activeRoles.length}
                badge="Roles"
                badgeClass="bg-amber-100 text-amber-800"
                accentColor="#f59e0b"
                gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                icon="briefcase"
              />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ClientPipeline data={candidatesData} />
              <ClientMetrics data={candidatesData} />
              <ClientRoles data={candidatesData} />
              <ClientNPS data={npsData} clientName={clientName} />
            </div>
          </div>
        )}

        {activeSection === 'pipeline' && (
          <div className="fade-in">
            <ClientPipeline data={candidatesData} expanded />
          </div>
        )}

        {activeSection === 'roles' && (
          <div className="fade-in">
            <ClientRoles data={candidatesData} expanded />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-6xl mx-auto px-6 py-4 text-center">
          <p className="text-xs text-gray-400">
            Powered by <span style={{ color: BRAND_BLUE, fontWeight: 600 }}>Satellite Talent</span> &middot; Recruitment Analytics Platform
          </p>
        </div>
      </footer>
    </div>
  );
};

// KPI Card matching main dashboard style
const KPICard = ({ label, value, icon, gradient, accentColor, badge, badgeClass }) => {
  const icons = {
    users: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    check: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    clock: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    briefcase: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  };

  return (
    <div className="dashboard-card p-6 relative overflow-hidden slide-in-up">
      <div
        className="absolute right-0 top-0 h-full w-24"
        style={{ background: `linear-gradient(to left, ${accentColor}1a, transparent)` }}
      />
      <div className="flex items-center">
        <div className="rounded-full p-3 mr-4 shadow-lg flex-shrink-0" style={{ background: gradient }}>
          {icons[icon]}
        </div>
        <div>
          <p className="text-gray-500 text-sm font-medium">{label}</p>
          <div className="flex items-baseline">
            <p className="text-3xl font-bold text-gray-800">{value}</p>
            {badge && (
              <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${badgeClass}`}>{badge}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
